/**
 * `/account/follows`, Sports you follow (ISSUE-1400). The web twin of the
 * phone's Account > Sports you follow: every league grouped by sport, the
 * fan's followed ones checked, saved with one `/follows/set`.
 *
 * Followed sports are written back exactly as they were read. Web never
 * stores a sport-shaped follow (owner answer Q285), but the phone's picker
 * can, and this screen must not wipe what the phone saved. For the same
 * reason a followed league that is now hidden or coming soon is kept on Save
 * (ISSUE-1378), even though the screen does not show it as picked.
 *
 * Cancel and Back go through the shared discard confirm when there are
 * unsaved picks (`unsaved-changes-guard.mdc`).
 */
import { useFollowsSet, useProfileGet } from "@sportsmet/profile-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button, confirmDestructive, SavedNote, Skeleton } from "@sportsmet/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { showApiError, showOfflineError } from "../errors/apiErrorBus";
import { ACCOUNT_ROUTE } from "../features/editProfile/editProfileLogic";
import {
  buildFollowsGroups,
  FOLLOWS_CANCEL_LABEL,
  FOLLOWS_DISCARD_BODY,
  FOLLOWS_DISCARD_LABEL,
  FOLLOWS_DISCARD_TITLE,
  FOLLOWS_EMPTY_BODY,
  FOLLOWS_EMPTY_TITLE,
  FOLLOWS_GROUP_LABEL_SUFFIX,
  FOLLOWS_HEADING,
  FOLLOWS_LOAD_FAILED_BODY,
  FOLLOWS_LOAD_FAILED_TITLE,
  FOLLOWS_SAVE_LABEL,
  FOLLOWS_SAVED_NOTE,
  FOLLOWS_SAVING_LABEL,
  FOLLOWS_SUB,
  isFollowsDirty,
  leagueIdsToSave,
  resolveSavedLeagueIds,
} from "../features/follows/followsEditorModel";
import { LeagueToggleGroups } from "../features/follows/LeagueToggleGroups";
import { togglePinnedLeague } from "../features/scores/pinnedLeagues";
import { BackButton } from "../shell/BackLink";
import { SectionEmptyState, SectionScreen } from "../shell/SectionScreen";

const FollowsScreen = (): ReactNode => {
  const catalog = useLeagueCatalogGet({});
  const profileQuery = useProfileGet({});
  const follows = useFollowsSet();
  const navigate = useNavigate();

  const sports = catalog.data?.data?.sports ?? [];
  const profile = profileQuery.data?.data?.profile ?? null;

  /** What the server holds, as of the last load or save. */
  const [saved, setSaved] = useState<string[] | null>(null);
  const [draft, setDraft] = useState<string[] | null>(null);
  const [hasJustSaved, setHasJustSaved] = useState(false);

  const isReady = catalog.data?.data != null && profile !== null;

  useEffect(() => {
    if (!isReady || saved !== null) return;
    const initial = resolveSavedLeagueIds({
      sports,
      followedLeagueIds: profile.followedLeagueIds,
    });
    setSaved(initial);
    setDraft(initial);
  }, [isReady, saved]);

  const isDirty =
    saved !== null && draft !== null && isFollowsDirty({ saved, draft });

  async function leave(): Promise<void> {
    if (isDirty) {
      const isConfirmed = await confirmDestructive({
        title: FOLLOWS_DISCARD_TITLE,
        body: FOLLOWS_DISCARD_BODY,
        confirmLabel: FOLLOWS_DISCARD_LABEL,
      });
      if (!isConfirmed) return;
    }
    void navigate({ to: ACCOUNT_ROUTE });
  }

  async function handleSave(): Promise<void> {
    if (draft === null || profile === null) return;
    let result;
    try {
      result = await follows.mutateAsync({
        followedSportIds: profile.followedSportIds,
        followedLeagueIds: leagueIdsToSave({
          sports,
          draft,
          followedLeagueIds: profile.followedLeagueIds,
        }),
      });
    } catch {
      showOfflineError();
      return;
    }
    if (showApiError(result)) return;
    setSaved(draft);
    setHasJustSaved(true);
  }

  const back = (
    <BackButton
      label="Account"
      onBack={() => {
        void leave();
      }}
    />
  );

  const hasFailed =
    catalog.isError ||
    profileQuery.isError ||
    catalog.data?.error != null ||
    profileQuery.data?.error != null;

  if (hasFailed) {
    return (
      <SectionScreen heading={FOLLOWS_HEADING} back={back}>
        <div data-testid="follows-failed">
          <SectionEmptyState
            headline={FOLLOWS_LOAD_FAILED_TITLE}
            body={FOLLOWS_LOAD_FAILED_BODY}
          />
        </div>
      </SectionScreen>
    );
  }

  const groups = buildFollowsGroups(sports);

  return (
    <SectionScreen heading={FOLLOWS_HEADING} back={back}>
      <p className="text-body text-ink-secondary">{FOLLOWS_SUB}</p>
      {draft === null ? (
        <div data-testid="follows-loading" className="flex flex-col gap-3">
          <Skeleton shape="text" />
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
      ) : groups.length === 0 ? (
        <div data-testid="follows-empty">
          <SectionEmptyState
            headline={FOLLOWS_EMPTY_TITLE}
            body={FOLLOWS_EMPTY_BODY}
          />
        </div>
      ) : (
        <div data-testid="follows-screen" className="flex flex-col gap-4">
          <LeagueToggleGroups
            groups={groups}
            selectedLeagueIds={draft}
            onToggle={(leagueId) => {
              setHasJustSaved(false);
              setDraft(
                togglePinnedLeague({ pinnedLeagueIds: draft, leagueId }),
              );
            }}
            testIdPrefix="follows-league-"
            groupLabelSuffix={FOLLOWS_GROUP_LABEL_SUFFIX}
          />
          <div className="sm-screen-bottom flex flex-col gap-2 pt-2">
            <SavedNote saved={hasJustSaved} label={FOLLOWS_SAVED_NOTE} />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  void leave();
                }}
                data-testid="follows-cancel"
              >
                {FOLLOWS_CANCEL_LABEL}
              </Button>
              <Button
                className="flex-1"
                disabled={!isDirty}
                loading={follows.isPending}
                loadingLabel={FOLLOWS_SAVING_LABEL}
                onClick={() => {
                  void handleSave();
                }}
                data-testid="follows-save"
              >
                {FOLLOWS_SAVE_LABEL}
              </Button>
            </div>
          </div>
        </div>
      )}
    </SectionScreen>
  );
};

export const Route = createFileRoute("/account_/follows")({
  beforeLoad: requireSignedIn,
  component: FollowsScreen,
});
